import { IconButton } from '@lastbrand/web-ui/icon-button'
import { SaveIcon, SaveSolidIcon } from '@lastbrand/web-ui/icons'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useCallback, useState, useSyncExternalStore } from 'react'

import { useProductWishList } from '@src/hooks/useProductWishlist'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import wishlistStateManager from '@src/quince-site-common/src/redux/globalStateManager/WishlistStateManager'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

import type { AddToWishlistButtonProps } from './add-to-wishlist-button.types'

const ACTIVE_HEART_CSS = css.raw({
  color: 'black',
  _hover: { color: 'gray', bg: 'transparent' },
  _active: { color: 'mediumgray', bg: 'transparent' },
})

const INACTIVE_HEART_CSS = css.raw({
  // Outline heart carries a 30%-white interior fill — in the design but absent
  // from the Figma handoff spec (`custom/white-trans-30`).
  color: 'mediumgray',
  '& svg': { fill: 'white-trans-30' },
  _hover: { color: 'gray', bg: 'transparent' },
  _active: { color: 'black', bg: 'transparent' },
})

export function AddToWishlistButton({
  spid,
  selectedColor,
  wishlistTrackConfig,
  wishlistEnabled = true,
  hideWishlistIcon = false,
  productName,
  wishlistAriaLabel,
  wishlistAriaLabelSelected,
  tileVariant = 'default',
  wishlistButtonCss,
  wishlistDisabled,
}: AddToWishlistButtonProps) {
  const { t } = useTranslation('common')
  const { isNativeView } = useDeviceInfo()
  const isLoggedIn = isBrowser() ? AuthUtil.isLoggedIn() : false
  const { isItemInWishList, addItemToWishList, removeItemFromWishList } = useProductWishList()

  // Read the wishlist as an external store instead of mirroring it into local state:
  //  - `getSnapshot` reflects the live wishlist and re-runs whenever the store publishes (async
  //    hydration on login via updateWishListOnLoggedIn, and every add/remove);
  //  - `getServerSnapshot` returns the storage-less server value so SSR/hydration stay in sync.
  // This keeps the heart correct across the remounts the DS grid triggers on variant change — the
  // remounted tile reads the store on its first paint, so there is no empty-heart flash.
  const subscribe = useCallback((onStoreChange: () => void) => wishlistStateManager.subscribe(onStoreChange), [])
  const storedWishlisted = useSyncExternalStore(
    subscribe,
    () => isItemInWishList(spid, selectedColor),
    () => false,
  )

  // Optimistic override held only while a toggle is in flight, then handed back to the store truth.
  const [pending, setPending] = useState<boolean | null>(null)
  const isWishlisted = pending ?? storedWishlisted

  const resolvedAriaLabel = wishlistAriaLabel ?? t('pc_add_wishlist_aria', { productName: productName ?? '' })
  const resolvedAriaLabelSelected =
    wishlistAriaLabelSelected ?? t('pc_remove_wishlist_aria', { productName: productName ?? '' })

  const isMini = tileVariant === 'mini'
  const isCompact = tileVariant === 'compact'
  const isNarrow = isCompact || isMini

  const showWishlist = wishlistEnabled && !(isNativeView && !isLoggedIn) && !hideWishlistIcon

  const onWishlistToggle = useCallback(() => {
    const next = !isWishlisted
    setPending(next)
    const op = next
      ? addItemToWishList(spid, selectedColor, wishlistTrackConfig ?? {})
      : removeItemFromWishList(spid, selectedColor, wishlistTrackConfig ?? {})
    op.catch((e: unknown) => {
      console.error(e)
    }).finally(() => {
      setPending(null)
    })
  }, [addItemToWishList, isWishlisted, removeItemFromWishList, selectedColor, spid, wishlistTrackConfig])

  if (!showWishlist) return null

  return (
    <div
      className={css({
        position: 'absolute',
        top: { base: 'space-8', lg: isNarrow ? 'space-8' : 'space-16' },
        right: { base: 'space-8', lg: isNarrow ? 'space-8' : 'space-16' },
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      })}
    >
      <IconButton
        type="button"
        variant="ghost"
        aria-label={isWishlisted ? resolvedAriaLabelSelected : resolvedAriaLabel}
        aria-pressed={isWishlisted}
        onClick={onWishlistToggle}
        disabled={wishlistDisabled}
        css={{
          flexShrink: 0,
          bg: 'transparent',
          ...(isWishlisted ? ACTIVE_HEART_CSS : INACTIVE_HEART_CSS),
          ...wishlistButtonCss,
        }}
      >
        {isWishlisted ? <SaveSolidIcon width={20} height={20} /> : <SaveIcon width={20} height={20} />}
      </IconButton>
    </div>
  )
}
